
import { collection, query, where, limit, getDocs, doc, updateDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import type { FeedbackItem, FeedbackReply } from '../types';
import { injectLogAndNotif, handleFirestoreError, OperationType } from './firebaseAPI';
import { sanitizeForFirestore } from '../utils/helpers';

const COLLECTION_NAME = 'feedback';

/**
 * Cư dân gửi phản hồi mới
 */
export const submitFeedback = async (feedback: Omit<FeedbackItem, 'id'>) => {
    try {
        const batch = writeBatch(db);
        const feedbackRef = doc(collection(db, COLLECTION_NAME));
        const feedbackId = feedbackRef.id;
        
        const sanitizedData = sanitizeForFirestore({ ...feedback, id: feedbackId });
        batch.set(feedbackRef, sanitizedData);

        // Tạo thông báo cho Admin & Log
        injectLogAndNotif(batch, {
            actionType: 'CREATE',
            module: 'Feedback',
            description: `Phản hồi mới từ căn ${feedback.residentId}: ${feedback.subject}`,
            performedBy: { name: 'Resident', email: feedback.residentId },
            ids: [feedbackId],
            linkTo: 'feedbackManagement'
        });

        await batch.commit();
        return feedbackId;
    } catch (error) {
        console.error("Lỗi gửi phản hồi:", error);
        throw error;
    }
};

/**
 * Tối ưu: Chuyển sang getDocs thay vì onSnapshot để tiết kiệm Quota.
 * Admin sẽ bấm nút "Làm mới" hoặc load lại trang để thấy dữ liệu mới.
 */
export const fetchActiveFeedback = async (): Promise<FeedbackItem[]> => {
    const q = query(
        collection(db, COLLECTION_NAME),
        where('status', 'in', ['Pending', 'Processing']),
        limit(50)
    );

    try {
        const snapshot = await getDocs(q);
        return snapshot.docs
            .map(d => ({ ...d.data(), id: d.id } as FeedbackItem))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } catch (error) {
        console.error("Lỗi fetch feedback:", error);
        return [];
    }
};

/**
 * Tải phản hồi cũ (Resolved) theo tháng
 */
export const fetchResolvedFeedback = async (period: string): Promise<FeedbackItem[]> => {
    const q = query(
        collection(db, COLLECTION_NAME),
        where('status', '==', 'Resolved'),
        limit(100)
    );
    
    try {
        const snap = await getDocs(q);
        const allResolved = snap.docs.map(d => ({ ...d.data(), id: d.id } as FeedbackItem));
        
        return allResolved
            .filter(item => item.date.startsWith(period))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } catch (error) {
        console.error("Lỗi fetch resolved feedback:", error);
        return [];
    }
};

export const replyFeedback = async (
    feedbackId: string, 
    updatedReplies: FeedbackReply[], 
    status: FeedbackItem['status'],
    residentId: string,
    adminInfo?: { name: string, email: string }
) => {
    const batch = writeBatch(db);
    const feedbackRef = doc(db, COLLECTION_NAME, feedbackId);

    batch.update(feedbackRef, {
        replies: updatedReplies,
        status: status
    });

    // Thông báo cho cư dân
    const resNotifRef = doc(collection(db, 'notifications'));
    batch.set(resNotifRef, {
        userId: residentId,
        title: 'BQL đã phản hồi yêu cầu',
        body: status === 'Resolved' ? 'Yêu cầu của bạn đã hoàn thành.' : 'BQL đã gửi tin nhắn mới.',
        type: 'feedback',
        link: 'portalContact',
        isRead: false,
        createdAt: serverTimestamp()
    });

    // Log cho admin
    injectLogAndNotif(batch, {
        actionType: 'UPDATE',
        module: 'Feedback',
        description: `Trả lời phản hồi căn ${residentId}. Trạng thái: ${status}`,
        performedBy: adminInfo || { name: 'Admin', email: 'system' },
        ids: [feedbackId],
        linkTo: 'feedbackManagement'
    });

    try {
        await batch.commit();

        // 3. Gửi thông báo Push (nếu cư dân có fcmToken)
        try {
            const userRef = doc(db, 'users', residentId);
            const userSnap = await getDocs(query(collection(db, 'users'), where('Email', '==', residentId), limit(1)));
            
            if (!userSnap.empty) {
                const userData = userSnap.docs[0].data();
                const fcmToken = userData.fcmToken;

                if (fcmToken) {
                    await fetch('/api/send-push', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            token: fcmToken,
                            title: 'Cổng Cư Dân: Phản hồi mới',
                            body: status === 'Resolved' ? 'Yêu cầu của bạn đã hoàn thành.' : 'BQL đã gửi tin nhắn mới.',
                            data: {
                                type: 'feedback',
                                feedbackId: feedbackId,
                                badgeCount: "1"
                            }
                        })
                    });
                }
            }
        } catch (pushError) {
            console.error('Lỗi gửi thông báo Push:', pushError);
            // Không throw lỗi này để tránh làm gián đoạn luồng chính
        }
    } catch (error) {
        handleFirestoreError(error, OperationType.UPDATE, COLLECTION_NAME);
    }
};

/**
 * Xoá ảnh đính kèm để tiết kiệm dung lượng (sau khi đã xử lý xong)
 */
export const deleteFeedbackMedia = async (feedbackId: string) => {
    try {
        const feedbackRef = doc(db, COLLECTION_NAME, feedbackId);
        await updateDoc(feedbackRef, {
            imageUrl: null
        });
    } catch (error) {
        console.error("Lỗi xoá ảnh feedback:", error);
        throw error;
    }
};
